import {
  ManageSeriesParticipant,
  SeriesDefinitionPart,
} from '@api/competitionsApi/interfaces';
import { TARGET_RING_VALUES } from './constants';

export const buildParticipantScoreDraft = (
  participant: ManageSeriesParticipant,
  parts: SeriesDefinitionPart[]
): Record<number, string> => {
  return parts.reduce<Record<number, string>>((draft, part) => {
    if (part.inputType === 'target_count') {
      return draft;
    }

    const score = participant.partScores.find(
      (partScore) => partScore.seriesDefinitionPartId === part.id
    );

    draft[part.id] =
      score?.value !== null && score?.value !== undefined
        ? String(score.value)
        : '';

    return draft;
  }, {});
};

export const buildParticipantTargetCountDraft = (
  participant: ManageSeriesParticipant,
  partId: number
): Record<string, number> => {
  const score = participant.partScores.find(
    (partScore) => partScore.seriesDefinitionPartId === partId
  );
  const counts = score?.inputPayload?.counts ?? {};

  return TARGET_RING_VALUES.reduce<Record<string, number>>((draft, ring) => {
    draft[ring] = Number(counts[ring] ?? 0);

    return draft;
  }, {});
};

export const parseScoreInput = (value: string): number | null => {
  const trimmed = value.trim();

  if (trimmed === '') {
    return null;
  }

  const parsed = Number(trimmed.replace(',', '.'));

  return Number.isFinite(parsed) ? parsed : null;
};

export const formatSeriesTotal = (totalValue: number | null): string => {
  if (totalValue === null) {
    return '—';
  }

  return String(totalValue);
};

export const countTargetShots = (counts: Record<string, number>): number => {
  return TARGET_RING_VALUES.reduce(
    (total, ring) => total + (counts[ring] ?? 0),
    0
  );
};

export const canCompleteSeries = (
  participants: ManageSeriesParticipant[],
  partIds: number[]
): boolean => {
  if (partIds.length === 0 || participants.length === 0) {
    return true;
  }

  return participants.every((participant) =>
    partIds.every((partId) =>
      participant.partScores.some(
        (score) =>
          score.seriesDefinitionPartId === partId && score.value !== null
      )
    )
  );
};

export const formatParticipantScoresSummary = (
  participant: ManageSeriesParticipant,
  parts: SeriesDefinitionPart[]
): string => {
  return parts
    .map((part) => {
      const score = participant.partScores.find(
        (partScore) => partScore.seriesDefinitionPartId === part.id
      );

      if (part.inputType === 'target_count') {
        if (score?.value != null) {
          return String(score.value);
        }

        const counts = score?.inputPayload?.counts ?? {};
        const counted = countTargetShots(counts);

        if (counted === 0) {
          return '—';
        }

        return part.shotsPerSeries != null
          ? `${counted}/${part.shotsPerSeries}`
          : String(counted);
      }

      return formatSeriesTotal(score?.value ?? null);
    })
    .join(' · ');
};
